import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { MEDIA_POOL_SOURCE_MODES, MEDIA_POOL_SEARCH_MODES } from '../constants';
import { abortMediaPoolSearch } from '../../../../../redux/story/storyActions';
import { clearMediaPool } from '../../../../../redux/story/storyReducer';
import { getStoryMediaId, isStoryMediaUploading } from '../../../media/storyMediaItem';
import { activeSelectedMedia } from '../../../selectors/selectedMediaSelectors';
import { useProject } from '../../../../../context/ProjectContext';

function useMediaPoolSearch() {
  const dispatch = useDispatch();
  const { actions } = useProject();
  const searchPromiseRef = useRef(null);

  const { id: projectId } = useParams();

  const activeMedia = useSelector(activeSelectedMedia);
  const activeMediaId = getStoryMediaId(activeMedia);
  const isActiveUploading = isStoryMediaUploading(activeMedia);
  const sourceMode = useSelector((state) => state.story.mediaPoolSourceMode);
  const searchQuery = useSelector((state) => state.story.mediaPoolSearchQuery);
  const message = useSelector((state) => state.story.selectedMessage);

  const trimmedQuery = searchQuery.trim();
  const messageBasis = sourceMode === MEDIA_POOL_SOURCE_MODES.MESSAGE && !trimmedQuery ? message : null;

  useEffect(() => {
    const abortSearch = () => {
      abortMediaPoolSearch();
      searchPromiseRef.current?.abort();
      searchPromiseRef.current = null;
    };

    const startSearch = (args) => {
      abortSearch();
      searchPromiseRef.current = actions('searchMediaPool', args);
    };

    const clearPool = () => {
      abortSearch();
      dispatch(clearMediaPool());
    };

    if (trimmedQuery) {
      startSearch({
        mode: MEDIA_POOL_SEARCH_MODES.SEARCH,
        query: trimmedQuery,
        media: activeMedia,
        projectId,
      });
      return abortSearch;
    }

    if (sourceMode === MEDIA_POOL_SOURCE_MODES.MESSAGE) {
      const trimmedMessage = messageBasis?.trim() ?? '';

      if (!trimmedMessage) {
        clearPool();
        return abortSearch;
      }

      startSearch({
        mode: MEDIA_POOL_SEARCH_MODES.MESSAGE,
        message: trimmedMessage,
        media: activeMedia,
        projectId,
      });
      return abortSearch;
    }

    if (!activeMediaId || isActiveUploading) {
      clearPool();
      return abortSearch;
    }

    startSearch({
      mode: MEDIA_POOL_SEARCH_MODES.MEDIA,
      media: activeMedia,
      projectId,
    });

    return abortSearch;
  }, [
    actions,
    dispatch,
    messageBasis,
    activeMedia,
    activeMediaId,
    isActiveUploading,
    sourceMode,
    trimmedQuery,
    projectId,
  ]);
}

export default useMediaPoolSearch;
